// Le moteur commun des jeux d'Arceau Game (D-161, D-162) : Le Parcours au lot 1, Le Sablier au lot 2 sur le même moteur.
// Logique pure, horloge et hasard injectés. Ce qu'il sait faire, pour tous les jeux :
//  * des participants : des équipes (les chasubles de l'exercice, D-052) ou chacun pour soi ;
//  * des actions DÉCLARÉES par les participants (« Fait ! », « Raté », un tir marqué) et rangées dans un journal ;
//  * l'arbitrage du coach : invalider la dernière action déclarée d'un participant, sanctionner (le jeu dit comment),
//    annuler la dernière action, mettre tout le monde en pause ;
//  * le temps calé sur l'horloge (départ + pauses), comme la séance en direct : une tablette mise en veille retombe juste.
// Chaque action garde l'état d'avant de ce qu'elle touche (les participants, le commun) : annuler, c'est le remettre.
// Le jeu (src/domain/parcours.ts) décrit ses états et ses règles ; le moteur ne connaît ni plateau ni cartes.

export type GameMode = 'teams' | 'solo';

/** Un participant : une équipe (ses membres, des identifiants de personnes, pour l'affichage seulement) ou une personne. */
export interface Participant {
  id: string;
  name: string;
  color: string;
  ink: string;
  /** La lettre sur le maillot-pion. */
  letter: string;
  members: string[];
}

/** Le temps de la partie : départ, pause en cours, total des pauses (millisecondes de l'horloge). */
export interface Clock { startedAt: number; pausedAt: number | null; pausedMs: number }
export const startClock = (now: number): Clock => ({ startedAt: now, pausedAt: null, pausedMs: 0 });
/** Le temps de jeu écoulé (les pauses ne comptent pas). */
export const elapsedMs = (c: Clock, now: number) => Math.max(0, (c.pausedAt ?? now) - c.startedAt - c.pausedMs);
export const pauseClock = (c: Clock, now: number): Clock => (c.pausedAt !== null ? c : { ...c, pausedAt: now });
export const resumeClock = (c: Clock, now: number): Clock => (c.pausedAt === null ? c : { ...c, pausedAt: null, pausedMs: c.pausedMs + Math.max(0, now - c.pausedAt) });

/** Une ligne du journal : qui, quoi, quand (temps de jeu), et l'état d'avant de ce qu'elle a changé. */
export interface LogEntry<P, S> {
  seq: number;
  /** Temps de jeu, en millisecondes. */
  at: number;
  /** Le participant qui agit ; null : le coach (arbitrage) ou le jeu. */
  who: string | null;
  kind: string;
  text: string;
  /** Une action déclarée par le participant (« Fait ! ») : le coach peut l'invalider. */
  declared?: boolean;
  /** Un geste d'arbitrage : il ne s'annule pas lui-même et ne compte pas comme « la dernière action ». */
  arbitration?: boolean;
  before: { ps: Record<string, P>; shared?: S };
  /** Annulée par le coach (« undo ») ou invalidée (« invalid »). */
  undone?: 'undo' | 'invalid';
}

export interface GameState<P, S> {
  mode: GameMode;
  participants: Participant[];
  /** L'état de chaque participant, propre au jeu. */
  ps: Record<string, P>;
  /** L'état commun, propre au jeu (la pioche, le vainqueur…). */
  shared: S;
  log: LogEntry<P, S>[];
  seq: number;
  clock: Clock;
  over: boolean;
}

export function createGame<P, S>(mode: GameMode, participants: Participant[], init: (p: Participant) => P, shared: S, now: number): GameState<P, S> {
  return { mode, participants, ps: Object.fromEntries(participants.map(p => [p.id, init(p)])), shared, log: [], seq: 0, clock: startClock(now), over: false };
}

export const paused = (g: GameState<unknown, unknown>) => g.clock.pausedAt !== null;
export const gameMs = (g: GameState<unknown, unknown>, now: number) => elapsedMs(g.clock, now);

/** `coach` : un geste du coach qui reste annulable (une sanction) et passe même pendant la pause. */
export interface ActMeta { who: string | null; kind: string; text: string; declared?: boolean; arbitration?: boolean; coach?: boolean }
/** Ce qu'une action change : les participants touchés (leur nouvel état) et, s'il change, le commun. */
export interface Change<P, S> { ps?: Record<string, P>; shared?: S; over?: boolean }

/**
 * Applique une action et l'écrit au journal, avec l'état d'avant de ce qu'elle touche. Refusée (état rendu tel quel)
 * pendant une pause ou une fois la partie finie, sauf un geste d'arbitrage.
 */
export function act<P, S>(g: GameState<P, S>, now: number, meta: ActMeta, change: Change<P, S>): GameState<P, S> {
  if (g.over && !meta.arbitration) return g;
  if (paused(g) && !meta.arbitration && !meta.coach) return g;
  const touched = Object.keys(change.ps ?? {}).filter(id => id in g.ps);
  const before: LogEntry<P, S>['before'] = { ps: Object.fromEntries(touched.map(id => [id, g.ps[id]!])) };
  if (change.shared !== undefined) before.shared = g.shared;
  const seq = g.seq + 1;
  const entry: LogEntry<P, S> = { seq, at: gameMs(g, now), who: meta.who, kind: meta.kind, text: meta.text, before };
  if (meta.declared) entry.declared = true;
  if (meta.arbitration) entry.arbitration = true;
  return {
    ...g, seq,
    ps: { ...g.ps, ...Object.fromEntries(touched.map(id => [id, change.ps![id]!])) },
    shared: change.shared !== undefined ? change.shared : g.shared,
    over: change.over ?? g.over,
    log: [...g.log, entry],
  };
}

/** La dernière action qu'on peut encore annuler (pas un geste d'arbitrage, pas déjà annulée). */
export const lastUndoable = <P, S>(g: GameState<P, S>) => [...g.log].reverse().find(e => !e.arbitration && !e.undone) ?? null;

/** « Annuler la dernière action » : tout ce qu'elle avait changé revient comme avant. */
export function undoLast<P, S>(g: GameState<P, S>, now: number, text = 'annule la dernière action'): GameState<P, S> {
  const e = lastUndoable(g);
  if (!e) return g;
  const log = g.log.map(x => (x.seq === e.seq ? { ...x, undone: 'undo' as const } : x));
  const restored: GameState<P, S> = { ...g, log, ps: { ...g.ps, ...e.before.ps }, shared: e.before.shared !== undefined ? e.before.shared : g.shared, over: false };
  return act(restored, now, { who: null, kind: 'undo', text, arbitration: true }, {});
}

/** La dernière action déclarée d'un participant (son dernier « Fait ! »), encore valable. */
export const lastDeclared = <P, S>(g: GameState<P, S>, who: string, kinds?: string[]) =>
  [...g.log].reverse().find(e => e.who === who && e.declared && !e.undone && (!kinds || kinds.includes(e.kind))) ?? null;

/**
 * « Invalider » : le participant revient à son état d'avant sa dernière action déclarée ; cette action et celles qu'il a
 * faites depuis sont rayées du journal (les autres participants ne bougent pas).
 */
export function invalidateLast<P, S>(g: GameState<P, S>, who: string, now: number, text: string, kinds?: string[]): GameState<P, S> {
  const e = lastDeclared(g, who, kinds);
  if (!e || !(who in e.before.ps)) return g;
  const log = g.log.map(x => (x.seq >= e.seq && (x.seq === e.seq || x.who === who) && !x.arbitration && !x.undone ? { ...x, undone: 'invalid' as const } : x));
  return act({ ...g, log, over: false }, now, { who: null, kind: 'invalidate', text, arbitration: true }, { ps: { [who]: e.before.ps[who]! } });
}

/** Pause pour tous : le temps s'arrête, les actions attendent ; reprendre décale le départ d'autant. */
export function setPaused<P, S>(g: GameState<P, S>, on: boolean, now: number): GameState<P, S> {
  if (on === paused(g)) return g;
  const clock = on ? pauseClock(g.clock, now) : resumeClock(g.clock, now);
  return act({ ...g, clock }, now, { who: null, kind: on ? 'pause' : 'resume', text: on ? 'pause pour tous' : 'reprise', arbitration: true }, {});
}

/** Les dernières lignes du journal, la plus récente d'abord, sans les annulées ni les pauses. */
export const recentLog = <P, S>(g: GameState<P, S>, n = 5) =>
  [...g.log].reverse().filter(e => !e.undone && !['pause', 'resume'].includes(e.kind)).slice(0, n);

/** « 8:05 » : un temps de jeu en minutes et secondes. */
export function fmtGameTime(ms: number): string {
  const s = Math.floor(Math.max(0, ms) / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Un hasard reproductible (tests, démo) : mulberry32. */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Mélange de Fisher-Yates, hasard injecté. */
export function shuffled<T>(xs: readonly T[], rand: () => number): T[] {
  const out = [...xs];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}
